import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, CircularProgress } from "@mui/material";
import { SxProps, Theme, useTheme } from "@mui/material/styles";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

const createStyles = (theme: Theme): { container: SxProps<Theme>; button: SxProps<Theme> } => ({
  container: {
    justifyContent: "end",
    display: "flex",
    gap: theme.analysisLayout.sectionGap,
    flexWrap: "wrap",
  },
  button: {
    fontSize: theme.typography.button.fontSize as string | number | undefined,
    height: theme.analysisLayout.buttonHeight,
    minHeight: theme.analysisLayout.buttonHeight,
    boxSizing: "border-box" as const,
    padding: theme.analysisLayout.buttonPadding,
    whiteSpace: "nowrap" as const,
    "&.MuiButton-contained:hover": {
      height: theme.analysisLayout.buttonHeight,
      minHeight: theme.analysisLayout.buttonHeight,
      padding: theme.analysisLayout.buttonPadding,
      boxShadow: "none",
    },
    "&.MuiButton-outlined:hover": {
      height: theme.analysisLayout.buttonHeight,
      minHeight: theme.analysisLayout.buttonHeight,
      padding: theme.analysisLayout.buttonPadding,
      boxShadow: "none",
    },
  },
});

interface PanelActionsProps {
  onStopEditing: () => void;
  onApply?: () => void | Promise<void>;
  isApplying?: boolean;
}

export const PanelActions = ({ onStopEditing, onApply, isApplying = false }: PanelActionsProps): JSX.Element => {
  const { t } = useTranslation("Common", { keyPrefix: "buttons" });
  const theme = useTheme();
  const styles = createStyles(theme);

  return (
    <Box sx={styles.container}>
      <Button
        sx={styles.button}
        endIcon={<CloseIcon />}
        variant="outlined"
        color="primary"
        onClick={onStopEditing}
        disabled={isApplying}
      >
        {t("cancel")}
      </Button>
      <Button
        sx={styles.button}
        variant="contained"
        endIcon={isApplying ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
        color="primary"
        onClick={onApply ?? onStopEditing}
        disabled={isApplying}
      >
        {t("applyChanges")}
      </Button>
    </Box>
  );
};
